import React, { useContext, useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Group,
  Center,
  Stack,
  Text,
  Container,
  rem,
  LoadingOverlay,
  Button,
  ThemeIcon,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import Lottie from "react-lottie-player";
import Microblink from "./microblink/capture/Index";
import BlinkIdInBrowser, { hasUsableIdentityFields } from "./blinkid";
import BlinkIdDatamatch from "./blinkid/datamatch";
import BlinkIdBarcodeIntro from "./blinkid/barcode-intro";
import VeratadCapture from "./veratad/capture";
import VeratadFaceCapture from "../face-capture/veratad";
import { useParams } from "react-router-dom";
import { DataContext } from "../../context/DataContext";
import InstructionsCarousel from "./instructions-carousel";
import { Carousel } from "@mantine/carousel";
import Mitek from "./mitek/capture";
import { isTablet } from "react-device-detect";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { getDeviceSignalsUrl, getEdgeQueueUrl } from "../../utils/edge";
import useLocale from "../../hooks/useLocale";
import Handoff from "../handoff";
import CompactHandoff from "../../components/compact-handoff";
import useStatusCheck from "../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../hooks/useEdgeStatusCheck";
import { IconSunHigh, IconId, IconCapture, IconShieldX } from "@tabler/icons-react";
import VeratadIdDocCapture from "../id-doc/veratad-capture";
import VerificationProcessingScreen from "../../components/verification-processing-screen";
import { preloadFaceDetectionModels } from "../../hooks/useFaceDetection";
import { isKidSite } from "../../utils/isKidSite";
import { isDocumentSupplied, isSelfieSupplied } from "../../utils/suppliedImages";
import { trackEvent } from "../../utils/telemetry";
export default function Dcams({ isModular = false }) {
  const { sdk } = useParams();
  const { data, settings, setSessionState, text, dispatch, routeSequence, currentIndex } = useContext(DataContext);

  // BlinkID in-browser routes render through this page too — same intro,
  // instructions, translations, and handoff — with the capture component
  // swapped out below.
  const isBlinkId = routeSequence?.[currentIndex]?.path === "dcams/blinkid";
  const handoffPath = isBlinkId ? "dcams/blinkid" : "dcams/mitek";

  // Step settings from the dcams/blinkid route step:
  //   datamatch: { active, service, review, edit, reattempts } — when
  //     active, the extracted document data feeds an AgeMatch
  //     verification after the scan.
  //   fallback: { active, target, on_fail } — active exposes the
  //     intro's "Use a different ID" escape hatch into ID document
  //     picture verification. on_fail additionally routes a failed
  //     datamatch (attempts exhausted) or an unusable barcode
  //     extraction into that same fallback; without it those failures
  //     end the journey (FAIL) like any other step.
  //   scan_sides: "back_only" switches the intro to the barcode-first
  //     v2 screen.
  //   show_onboarding_guide: true re-enables the SDK's "Locate barcode
  //     on the document" modal shown over the camera before scanning
  //     starts. Off by default — the intro screen already covers it.
  const blinkIdRouteSettings = settings?.journey?.route?.find(
    (r) => r.path === "dcams/blinkid"
  )?.settings;
  const blinkIdDatamatch = blinkIdRouteSettings?.datamatch;
  const blinkIdFallback = blinkIdRouteSettings?.fallback;
  // Failure-driven fallback is an explicit opt-in on top of the user
  // choice: fallback.active alone only enables "Use a different ID".
  const blinkIdFallbackOnFail =
    blinkIdFallback?.active === true && blinkIdFallback?.on_fail === true;
  const blinkIdBarcodeMode = blinkIdRouteSettings?.scan_sides === "back_only";
  // Extracted fields from a completed BlinkID scan — non-null flips the
  // page from the capture UI to the datamatch step.
  const [blinkIdExtracted, setBlinkIdExtracted] = useState(null);
  // Fallback engaged: render this page as plain ID document picture
  // verification (same route step, no navigation) — the doc-capture
  // intro, the Microblink capture SDK, the queue POST, and status
  // polling all behave exactly like a dcams/mitek step.
  const [docFallbackActive, setDocFallbackActive] = useState(false);
  const showBlinkIdUi = isBlinkId && !docFallbackActive;

  const activateDocFallback = () => {
    // Drop the datamatch service the scan handler may have set — the
    // document-verification submission must not inherit AGEMATCH5.0.
    delete data.service;
    setBlinkIdExtracted(null);
    setDocFallbackActive(true);
  };

  const handleBlinkIdComplete = (result) => {
    const fields = result?.fields || {};
    if (blinkIdDatamatch?.active === true) {
      // Guardrail for flows where the user is never allowed to type
      // (review:false auto-submit, or review:true confirm-only): the
      // datamatch data must come off the barcode, so an unusable
      // extraction (e.g. a one-letter first name) must NOT surface the
      // editable form the shared machinery falls back to. Fail the
      // journey instead — or route into document verification when the
      // step opts into failure fallback.
      const userCanEditData =
        blinkIdDatamatch.review === true && blinkIdDatamatch.edit === true;
      if (!userCanEditData && !hasUsableIdentityFields(fields)) {
        console.warn(
          "[BlinkID] extracted fields unusable for datamatch — failing",
          fields
        );
        if (blinkIdFallbackOnFail) {
          activateDocFallback();
        } else {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        }
        return;
      }
      // Merge the document data into the context `data` object so the
      // datamatch form (and its submission payload) prefills from it —
      // the same mechanism every data-collection flow uses.
      ["fn", "ln", "dob", "addr", "city", "state", "zip"].forEach((key) => {
        if (fields[key]) data[key] = fields[key];
      });
      if (blinkIdDatamatch.service) {
        data.service = blinkIdDatamatch.service;
      }
      setBlinkIdExtracted(fields);
    } else {
      console.log("[BlinkID] scan complete — datamatch inactive", fields);
    }
  };
  const theme = useMantineTheme();
  const { isTrulyMobile, isEmulated, serverTamperDetected, serverCheckPending } = useDeviceDetection({
    requestId: settings?.request_id,
    serverReportUrl: getDeviceSignalsUrl(),
  });
  const blockedLocale = useLocale("device-blocked");
  // Intro-screen copy comes from the local document-capture locale set
  // (en default + 44 translations) rather than the backend `text.dcams`
  // set, which only ships a handful of languages (and Serbian only in
  // Cyrillic). Backend text stays as a fallback for safety.
  const docLocale = useLocale("document-capture");
  // Journey API prefill (utils/suppliedImages.js), same rules as
  // VeratadIdDocCapture: a supplied front completes the document phase;
  // with the selfie supplied too (or none required) nothing is captured.
  // BlinkID reads a barcode and uploads nothing, so it never prefills.
  // Read once at mount; routes.js holds the step until the images land.
  const [prefill] = useState(() => {
    const docSupplied = !isBlinkId && isDocumentSupplied(settings);
    const selfieRequired = !!settings?.journey?.settings?.isSelfie;
    return {
      document: docSupplied,
      selfieOnly: docSupplied && selfieRequired && !isSelfieSupplied(settings),
      complete: docSupplied && (!selfieRequired || isSelfieSupplied(settings)),
    };
  });
  const [isSDKActive, setIsSDKActive] = useState(false);
  const [isSDKComplete, setIsSDKComplete] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [getFace, setGetFace] = useState(prefill.selfieOnly);
  const [gotFace, setGotFace] = useState(
    prefill.complete && !!settings?.journey?.settings?.isSelfie
  );
  const [status, setStatus] = useState("");
  const [onMobile, setOnMobile] = useState(isTrulyMobile);
  const [qrScanned, setQrScanned] = useState(false);
  const [useFallbackCapture, setUseFallbackCapture] = useState(false);
  const language = (
    data?.language ||
    settings?.language ||
    settings?.text?.language ||
    text?.language ||
    ""
  ).toLowerCase();
  const isJapanese = language === "ja";

  // ── ID-doc capture provider selection ──
  // Currently: kid-like sites → veratad, everything else → microblink.
  const siteNameLower = (settings?.site_name || "").toLowerCase();

  // Check if any route in the journey has is_japan enabled
  const isJapanFrontOnly = Array.isArray(settings?.journey?.route) &&
    settings.journey.route.some(r => r.settings?.is_japan === true);
  // Journey config wins over the site-name heuristic: a blinkid route
  // must never be hijacked into the kid-site veratad capture flow.
  const useVeratadCapture = isKidSite(siteNameLower) && !isJapanFrontOnly && !isBlinkId;
  // Which capture module actually runs this step — stamped onto the
  // page-level telemetry events (blinkid falls back to the microblink
  // capture path when its doc fallback engages).
  const captureProvider = useVeratadCapture
    ? "veratad"
    : showBlinkIdUi
      ? "blinkid"
      : "microblink";
  const should_show_continue_on_desktop_flag = settings?.shouldShowContinueOnDesktop === true;
  const isHandoffMode = !onMobile && !should_show_continue_on_desktop_flag;

  // Funnel top: the doc-capture step rendered (intro / handoff /
  // capture, any provider) — everything upstream of the Get Started
  // click (id_doc_capture_started) anchors to this. `once` dedupes
  // against VeratadIdDocCapture firing the same event when this page
  // hosts it.
  useEffect(() => {
    // Veratad-capture sites: VeratadIdDocCapture fires its own
    // id_doc_landed carrying the capture mode — don't beat it to the
    // `once` dedup with a mode-less event.
    if (useVeratadCapture) return;
    trackEvent(
      "id_doc_landed",
      { provider: isBlinkId ? "blinkid" : "microblink" },
      { once: true }
    );
  }, []);

  // Update onMobile when device detection changes (e.g., emulation toggled)
  // Skip if showHandoffOnTablet is active — that setting takes priority on tablets
  useEffect(() => {
    if (settings?.journey?.settings?.showHandoffOnTablet && isTablet) return;
    console.log("🔍 DCAMS: isTrulyMobile changed to", isTrulyMobile, "isEmulated:", isEmulated);
    setOnMobile(isTrulyMobile);
  }, [isTrulyMobile, isEmulated]);

  useEffect(() => {
    if (data?.isReactNative) {
      setOnMobile(true);
    }
  }, [data?.isReactNative]);

  useEffect(() => {
    // If showHandoffOnTablet is enabled and user is on tablet, start with QR visible (onMobile = false)
    if (settings?.journey?.settings?.showHandoffOnTablet && isTablet) {
      setOnMobile(false);
    }
  }, [settings?.journey?.settings?.showHandoffOnTablet]);

  // Preload face-api models during the doc-scan phase when the
  // journey also requires a selfie. The user spends 10–60s on the
  // doc instructions, scan, upload, and backend processing — more
  // than enough background time to download the ~560KB of models
  // even on slow CN routes. By the time `getFace` flips and
  // VeratadFaceCapture mounts, models are already loaded and the
  // face-capture intro's gate immediately resolves. No button
  // gating here — the doc-scan step doesn't depend on face-api.
  useEffect(() => {
    if (settings?.journey?.settings?.isSelfie && !isSelfieSupplied(settings)) {
      preloadFaceDetectionModels().catch(() => {
        // Hook will retry + surface the real error when capture mounts.
      });
    }
  }, [settings?.journey?.settings?.isSelfie]);

  // currentPath sent with every status poll. The backend compares it
  // against the journey's last route to decide isLastRoute, and the
  // legacy statusCheck only finalizes a FAIL when isLastRoute is true
  // (otherwise it answers PENDING + enhanced_next_document:"false",
  // the "advance to the next route" signal). On the blinkid route —
  // including its document-verification fallback — the journey path is
  // "dcams/blinkid", so polling as "dcams/mitek" would report
  // isLastRoute:false and a failed verification would never finalize.
  const cp = isModular
    ? "dcams/modular"
    : isBlinkId
      ? "dcams/blinkid"
      : "dcams/mitek";
  const isSmallMobile = useMediaQuery("(max-width: 395px)");

  // Captured terminal status from the polling hook. Set by
  // captureTerminal; consumed by VerificationProcessingScreen via
  // resultReady. The screen owns the eventual SET_STATUS +
  // SET_IS_CURRENT_COMPONENT_COMPLETE dispatch so navigation waits
  // for "Completing verification…" to play.
  const [terminalStatus, setTerminalStatus] = useState(null);

  const captureTerminal = useCallback((result) => {
    if (!result) return true;
    const status = result.status;
    const processState = result.process_state;
    const isTerminal =
      processState === "NEXT_APP_TRIGGER" ||
      processState === "COMPLETED" ||
      status === "PASS" ||
      status === "FAIL";
    if (isTerminal) {
      trackEvent("id_doc_verification_done", { provider: captureProvider, status, process_state: processState });
    }
    if (processState === "NEXT_APP_TRIGGER") {
      setTerminalStatus("NEXT_APP");
      return false;
    }
    if (processState === "COMPLETED") {
      setTerminalStatus(status === "PASS" ? "PASS" : "FAIL");
      return false;
    }
    if (status === "PASS") {
      setTerminalStatus("PASS");
      return false;
    }
    if (status === "FAIL") {
      setTerminalStatus("FAIL");
      return false;
    }
    return true;
  }, []);

  const handleProcessingComplete = useCallback(() => {
    if (terminalStatus === "NEXT_APP") {
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      dispatch({ type: "TRIGGER_NAVIGATION" });
      return;
    }
    dispatch({ type: "SET_STATUS", payload: terminalStatus || "FAIL" });
    dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
  }, [terminalStatus, dispatch]);

  // For doc+selfie flows the backend will return NEXT_APP_TRIGGER as
  // soon as the document side lands, even though we still need to
  // collect the selfie. Don't start polling (or render the processing
  // screen) until the selfie has also been captured, otherwise the
  // screen completes the journey before the face-capture redirect
  // useEffect can run.
  const needsSelfieNext =
    settings?.journey?.settings?.isSelfie && !gotFace;
  const shouldPoll = isSDKComplete && !needsSelfieNext;

  // Use edge status check when edge is true
  if (data?.edge === true) {
    useEdgeStatusCheck(shouldPoll, captureTerminal);
  } else {
    useStatusCheck(shouldPoll, false, cp, captureTerminal);
  }

  useEffect(() => {
    if (isSDKComplete) {
      if (settings.journey.settings?.isSelfie && !gotFace) {
        setIsUploading(false);
        setIsSDKComplete(false);
        setGetFace(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      data.settings = settings;
      data.type = "SDK_Scan";

      // Check if edge mode is enabled
      const edgeQueueUrl = getEdgeQueueUrl();

      const apiUrl = data?.edge === true
        ? edgeQueueUrl
        : "https://vx.dcams.app/frontend/dcams/process";

      // Add queue parameter for edge mode
      const requestBody = data?.edge === true
        ? { ...data, queue: "dcams" }
        : data;

      trackEvent("id_doc_processing_requested", { provider: captureProvider });
      fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (result) {
          setLoading(false);
        })
        .catch(function () {
          alert("Something Went Wrong! Please try again.");
        });
    }
  }, [isSDKComplete, gotFace]);

  const handleFaceCaptureComplete = () => {
    setIsSDKComplete(true);
    setGotFace(true);
  };

  // Prefill, every image supplied: submit straight away. Kid sites hand
  // the whole step to VeratadIdDocCapture, which applies the same rule
  // itself, so this page must not submit as well.
  useEffect(() => {
    if (useVeratadCapture || !prefill.complete) return;
    trackEvent("id_doc_prefill_applied", { provider: captureProvider, next: "processing" });
    setIsSDKComplete(true);
  }, []);

  // kid-like sites use veratad capture directly
  if (useVeratadCapture) {
    return <VeratadIdDocCapture />;
  }

  // BlinkID scan finished with datamatch active → run the AgeMatch
  // verification with the extracted document data (review form or
  // auto-submit, per the step's datamatch config). A terminal FAIL
  // routes into document verification instead of failing the journey
  // only when the step opts in via fallback.on_fail.
  if (showBlinkIdUi && blinkIdExtracted && blinkIdDatamatch?.active === true) {
    return (
      <BlinkIdDatamatch
        datamatch={blinkIdDatamatch}
        onFallback={blinkIdFallbackOnFail ? activateDocFallback : undefined}
      />
    );
  }

  // Render the processing screen as soon as the SDK starts uploading
  // images (isUploading) — not just after the SDK reports complete
  // (isSDKComplete). The screen opens on "Uploading your document…"
  // which fits the upload moment, and stays mounted through to the
  // backend verification so its stage progression doesn't reset.
  // resultReady stays false until captureTerminal lands a terminal
  // status from polling, which only kicks in once isSDKComplete=true.
  // Skip when we still owe the journey a selfie — the dcams useEffect
  // will redirect to face capture instead, and showing the processing
  // screen here makes the wrong UI flash for a frame.
  // prefill.complete renders here from the first frame, ahead of the
  // device check and desktop handoff below: nothing is captured on this
  // device, so a desktop user is not sent to a phone.
  if (isSDKComplete || isUploading || prefill.complete) {
    // Three flow-type buckets:
    //   - doc-only → "document" (uploading → auth → validity → details → completing)
    //   - doc-selfie before face capture → "document-upload" (single stage,
    //     holds on "Uploading your document…" until the redirect useEffect
    //     hands off to face capture)
    //   - doc-selfie after face capture → "document-selfie" (verifying →
    //     face-to-doc → liveness → completing)
    // Polling is already gated by shouldPoll so the doc-upload type
    // never fires onComplete.
    let flowType = "document";
    if (settings?.journey?.settings?.isSelfie) {
      flowType = needsSelfieNext ? "document-upload" : "document-selfie";
    }
    return (
      <VerificationProcessingScreen
        type={flowType}
        // Prefill: the document was never uploaded from this device.
        skipStages={prefill.document ? ["uploading"] : undefined}
        resultReady={terminalStatus !== null}
        onComplete={handleProcessingComplete}
      />
    );
  }

  const handleStart = () => {
    setIsSDKActive(true);
  };

  // Show loading overlay while server-side device check is pending
  if (serverCheckPending) {
    return <LoadingOverlay visible />;
  }

  // Block session if server-side tamper detection flagged this request
  if (serverTamperDetected) {
    return (
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          padding: "20px",
        }}
      >
        <ThemeIcon variant="light" color="red" size={80} radius="xl">
          <IconShieldX size={50} stroke={1.5} />
        </ThemeIcon>
        <Text fz={24} fw={700} align="center" mt="lg">
          {blockedLocale?.blocked?.title || "Unable to Continue"}
        </Text>
        <Text fz="md" c="dimmed" align="center" mt="xs" maw={400}>
          {blockedLocale?.blocked?.message || "Please try again on your mobile device."}
        </Text>
      </Box>
    );
  }

  // We now use the onMobile state defined above

  // Check if journey settings enable handoff on tablets
  const showHandoffOnTablet = settings?.journey?.settings?.showHandoffOnTablet === true;
  const shouldShowQROnTablet = showHandoffOnTablet && isTablet;

  // Show handoff with continue button on tablets when showHandoffOnTablet is enabled
  if (shouldShowQROnTablet && !onMobile) {
    return (
      <>
        <Handoff nextPath={handoffPath} />
        <Button onClick={() => setOnMobile(true)} mt='sm' fullWidth variant="subtle">
          {text.qr?.troublePrimaryButton || "Continue on this device"}
        </Button>
      </>
    );
  }

  // Check if shouldShowContinueOnDesktop is explicitly set to true in settings
  const should_show_continue_on_desktop = settings?.shouldShowContinueOnDesktop === true;

  // Only show full handoff page if NOT on mobile and shouldShowContinueOnDesktop is false
  if (!onMobile && !should_show_continue_on_desktop) {
    return <Handoff nextPath={handoffPath} />;
  }

  const instructions = [
    { Icon: IconSunHigh, instruction: docLocale?.setup?.wellLit || text?.dcams?.instructions?.[0] },
    { Icon: IconId, instruction: docLocale?.setup?.flatSurface || text?.dcams?.instructions?.[1] },
    { Icon: IconCapture, instruction: docLocale?.setup?.allCorners || text?.dcams?.instructions?.[2] },
  ];

  return getFace ? (
    <VeratadFaceCapture onCompletion={handleFaceCaptureComplete} />
  ) : useFallbackCapture ? (
    <VeratadIdDocCapture forceStupidMode />
  ) : (
    <>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between", // This will push the button to the bottom
          height: "100%", // This should take the full height of its parent
        }}
      >
        <Box style={{ overflowY: "auto", minHeight: 0 }}>
          {showBlinkIdUi && blinkIdBarcodeMode ? (
            // Barcode-first intro (v2 flow, screen 01). The escape
            // hatch into document verification only renders when the
            // route step's fallback is active.
            <BlinkIdBarcodeIntro
              onUseDifferentId={
                blinkIdFallback?.active === true
                  ? activateDocFallback
                  : undefined
              }
            />
          ) : (
          <Stack align="center" spacing={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}>
            <ThemeIcon
              variant="light"
              c={data.styles?.primaryColor ? data.styles.primaryColor : "teal"}
              size={(!onMobile && should_show_continue_on_desktop) ? 60 : 85}
              radius={"xl"}
            >
              <IconId size={(!onMobile && should_show_continue_on_desktop) ? 45 : 70} stroke={1} />
            </ThemeIcon>

            <Text component="h1" m={0} fz={(!onMobile && should_show_continue_on_desktop) ? 24 : (isSmallMobile ? 20 : 30)} fw={700} align="center">
              {docLocale?.setup?.title || text?.dcams?.title}
            </Text>
            <Text align="center" fz={(!onMobile && should_show_continue_on_desktop) ? "sm" : undefined}>{docLocale?.setup?.instructions || text?.dcams?.subTitle}</Text>
            <Paper
              withBorder
              p={(!onMobile && should_show_continue_on_desktop) ? "sm" : "md"}
              style={{
                backgroundColor:
                  data?.styles?.components?.paper?.backgroundColor ??
                  (theme.colorScheme === 'dark' ? theme.colors.dark[6] : "#fafafa"),
                width: "100%"
              }}
            >
              <Stack spacing={(!onMobile && should_show_continue_on_desktop) ? "md" : (isSmallMobile ? "xs" : "lg")}>
                {instructions.map(({ Icon, instruction }, index) => (
                  <Group
                    key={index}
                    spacing={isSmallMobile ? "xs" : "sm"} // Adjust spacing for very small screens
                    noWrap
                  >
                    {/* Render the icon dynamically with the size prop */}
                    <Icon size={(!onMobile && should_show_continue_on_desktop) ? 20 : (isSmallMobile ? 16 : 30)} />

                    {/* Text with responsive font size */}
                    <Text
                      fz={(!onMobile && should_show_continue_on_desktop) ? 14 : (isSmallMobile ? "12px" : "20px")}

                    >
                      {instruction}
                    </Text>
                  </Group>
                ))}
              </Stack>
            </Paper>
            {isJapanese && (
              <Text align="center" fz={isSmallMobile ? "12px" : "sm"}>
                マイナンバーカード：表面のみスキャンしてください。裏面は絶対にスキャンしないでください。
              </Text>
            )}
          </Stack>
          )}
        </Box>
        <Box>
          <Text fz="sm" color="dimmed" align="center" pb={(!onMobile && should_show_continue_on_desktop) ? 8 : 15}>
            {docLocale?.setup?.cameraPermission || text?.dcams?.note}
          </Text>

          {showBlinkIdUi ? (
            // BlinkID borrows only this page's UI shell (intro,
            // instructions, translations, handoff). isSDKComplete stays
            // false so the mitek-style queue submission, status polling,
            // and upload processing screen never run — extraction is
            // client-side and the datamatch submission drives journey
            // completion instead. When the doc fallback engages, this
            // flips to the Microblink branch and the full mitek-style
            // pipeline takes over.
            <BlinkIdInBrowser
              onComplete={handleBlinkIdComplete}
              onStart={handleStart}
            />
          ) : (
            <Microblink
              onUpload={() => setIsUploading(true)}
              onComplete={() => setIsSDKComplete(true)}
              onStart={handleStart}
              qrScanned={qrScanned}
              onFallbackRequested={() => setUseFallbackCapture(true)}
            />
          )}

          {/* Show compact handoff QR when continue on desktop is enabled and not on mobile */}
          {!onMobile && should_show_continue_on_desktop && !qrScanned && (
            <CompactHandoff
              nextPath={handoffPath}
              onQrScanned={setQrScanned}
            />
          )}
        </Box>
      </Box>
    </>
  );
}
